// Draw-only helpers extracted from game.ts (used by scene builders).

import { G } from './state';
import { SCREEN_W, SCREEN_H, SCREEN_CX, SCREEN_CY, S, BASE_TILE, BASE_PLAYERW, BASE_PLAYERH, TILE } from './constants';
import { Get, GetRoom } from './mapgen';
import { rooms } from './state';
import { getImg, makeFog, getAssetScale } from '../render/assets';
import {
  Surface,
  screen,
  blitGame,
  DrawRect,
  DrawCircleEx,
  draw_text,
} from '../render/surface';
import { profilerMeasure } from '../dev/profiler';
import { TILE_DRAW_MARGIN } from '../render/camera';

const tr = Math.trunc;

export function DrawMeter(x: number, y: number, n: number): void {
  const meter = getImg('dat/i/meter.png');
  meter.colorKey = 0;
  const meterNs = getAssetScale('dat/i/meter.png');
  const barH = 6 * meterNs;
  blitGame(meter, { x: 0, y: barH, w: 150 * meterNs, h: barH }, screen, S(x), S(y), meterNs);
  blitGame(meter, { x: 0, y: 0, w: n * 6 * meterNs, h: barH }, screen, S(x), S(y), meterNs);
}

let tilesSurf: Surface | null = null;
let fogSurf: Surface | null = null;

export function DrawLevel(
  off_x: number,
  off_y: number,
  hide_not_visited: number,
  fog_of_war: number,
  tilesX = 21,
  tilesY = 16
): void {
  profilerMeasure('DrawLevel', () => {
    DrawRect(0, 0, SCREEN_W, SCREEN_H, 255);
    if (tilesSurf === null) {
      tilesSurf = getImg('dat/i/tileset.png');
      fogSurf = makeFog(tilesSurf);
    }
    const tileScale = getAssetScale('dat/i/tileset.png');
    const srcTile = BASE_TILE * tileScale;
    const tileOffX = off_x % TILE;
    const tileOffY = off_y % TILE;
    const baseGX = tr(off_x / TILE);
    const baseGY = tr(off_y / TILE);
    const playerRoom = G.player_room;
    const m = TILE_DRAW_MARGIN;
    for (let y = -m; y < tilesY + m; y++) {
      for (let x = -m; x < tilesX + m; x++) {
        const resolve_x = x + baseGX;
        const resolve_y = y + baseGY;
        const groom = GetRoom(resolve_x, resolve_y);
        let tx: number;
        const visited = groom >= 0 ? rooms[groom].visited : 0;
        if (visited === 0 && playerRoom !== groom && hide_not_visited) {
          tx = 17 * srcTile;
        } else {
          tx = Get(resolve_x, resolve_y) * srcTile;
        }
        const sx = x * TILE - tileOffX;
        const sy = y * TILE - tileOffY;
        if (playerRoom !== groom && fog_of_war) {
          blitGame(fogSurf!, { x: tx, y: 0, w: srcTile, h: srcTile }, screen, sx, sy, tileScale);
        } else {
          blitGame(tilesSurf, { x: tx, y: 0, w: srcTile, h: srcTile }, screen, sx, sy, tileScale);
        }
      }
    }
  });
}

let playerSurf: Surface | null = null;
export function DrawPlayer(x: number, y: number, pl_dir: number, pl_frm: number): void {
  if (playerSurf === null) {
    playerSurf = getImg('dat/i/player.png');
    playerSurf.colorKey = 0;
  }
  const pScale = getAssetScale('dat/i/player.png');
  const fw = BASE_PLAYERW * pScale;
  const fh = BASE_PLAYERH * pScale;
  blitGame(playerSurf, { x: pl_frm * fw, y: pl_dir * fh, w: fw, h: fh }, screen, x, y, pScale);
}

export function DrawCircuit(): void {
  const bar = getImg('dat/i/circuit-bar.png');
  const barNs = getAssetScale('dat/i/circuit-bar.png');
  const trackW = 160 * barNs;
  const rowH = 12 * barNs;
  const innerPad = 2 * barNs;
  const innerW = 156 * barNs;
  const innerH = 8 * barNs;

  blitGame(bar, { x: 0, y: 0, w: trackW, h: rowH }, screen, S(110), S(469), barNs);
  draw_text(S(3), S(469), 'Psi Circuit', 200);

  if (G.magic_circuit === 0) return;

  const pct = Math.abs(G.magic_circuit) / G.circuit_size;
  const fillW = tr(innerW * pct);
  const fillRow = G.magic_circuit > 0 ? 1 : 2;
  blitGame(
    bar,
    { x: innerPad, y: fillRow * rowH + innerPad, w: fillW, h: innerH },
    screen,
    S(110) + S(2),
    S(469) + S(2),
    barNs
  );
  draw_text(S(115), S(470), (Math.abs(G.magic_circuit / 100.0)).toFixed(1), 0);
}

let shieldT = 0;
export function DrawShield(centerX = SCREEN_CX, centerY = SCREEN_CY): void {
  shieldT++;
  if (G.player_shield === 0) return;
  if (G.shield_hp === 0) return;
  let s_size = G.shield_hp;
  let belts = 0;
  if (s_size > 15) {
    belts = s_size - 15;
    s_size = 15;
  }
  DrawCircleEx(
    centerX,
    centerY,
    S(28 + s_size),
    S(28 - s_size),
    128 +
      tr((G.shield_hp * 127) / G.player_shield) -
      (50 * (G.shield_hp < G.player_shield ? 1 : 0) + G.shield_recover) -
      45 +
      (shieldT % 4) * 15
  );
  for (let i = 0; i < belts; i++) {
    const bpos = S(13) + tr((S(30) * (i + 1)) / (belts + 1));
    DrawCircleEx(centerX, centerY, bpos + S(1), bpos - S(1), ((i + shieldT) % 6) * 12);
  }
}

let artifactSpr: Surface | null = null;
export function DrawArtifacts(): void {
  if (artifactSpr === null) {
    artifactSpr = getImg('dat/i/artifacts.png');
    artifactSpr.colorKey = 0;
  }
  for (let i = 0; i < 12; i++) {
    if (G.artifacts[i]) {
      const artNs = getAssetScale('dat/i/artifacts.png');
      const icon = 32 * artNs;
      blitGame(artifactSpr, { x: i * icon, y: 0, w: icon, h: icon }, screen, S(608), S(47 + i * 35), artNs);
    }
  }
}

export function ScrollTo(x: number, y: number): void {
  if (G.scroll_home === 0) {
    G.scroll_x = x;
    G.scroll_y = y;
    return;
  }
  if (G.scroll_home === 1) {
    G.scroll_home = 2;
  }
  if (G.scroll_home === 2) {
    G.scroll_x += tr((x - G.scroll_x) / 2);
    G.scroll_y += tr((y - G.scroll_y) / 2);
    if (Math.abs(G.scroll_x - x) < S(2) && Math.abs(G.scroll_y - y) < S(2)) {
      G.scroll_x = x;
      G.scroll_y = y;
      G.scroll_home = 0;
    }
  }
}
